import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces/payerPayeeEngagement";
import queryString from "qs";
import { useHistory } from "react-router-dom";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { queriesPayees } from "@query";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";
import { useMutationArchivePayeeEngagements } from "../../../query/payeeEngagements/mutations/useMutationArchivePayeeEngagements";
import { useQueryPayeeOutstandingInvoices } from "../../../query/payments/queries";
import { selectorPayerPayeeEngagementName } from "../selectors/selectorPayerPayeeEngagementName";
import { ContractorInformation } from "./ContractorInformation";
import { useQueryPayeeEngagementsListByPayeeId } from "../../../query/payeeEngagements/queries/useQueryPayeeEngagementsListByPayeeId";

export interface ArchiveContractorModalProps {
  payeeId: string;
  onClose: () => void;
}

export const ArchiveContractorModal: React.FC<ArchiveContractorModalProps> = ({
  payeeId,
  onClose
}) => {
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();
  // Archive → v3 `deletePayee` (soft-deactivate → `Deactivated`). Restore has no
  // v3 activate endpoint, so the row "Restore" action stays on v1. See
  // V3_MIGRATION_TRACKER.md (payees).
  const xWingspanAccount = useXWingspanPayerId();
  const [archivePayee, archivePayeeMeta] =
    queriesPayees.deletePayee.useMutation();
  const [archiveEngagements, archiveEngagementsMeta] =
    useMutationArchivePayeeEngagements();

  const outstandingInvoicesQuery = useQueryPayeeOutstandingInvoices(payeeId);
  const queryPayeeEngagements = useQueryPayeeEngagementsListByPayeeId(payeeId);

  const engagements = queryPayeeEngagements.data;

  const hasOutstandingInvoices = !!outstandingInvoicesQuery.data?.length;

  const visibleEngagements = engagements?.filter(
    e => e.status !== PayerPayeeEngagementStatus.Inactive
  );

  return (
    <WSList gap="L">
      <WSText.ParagraphSm weight="book">
        Are you sure you want to archive this contractor?
      </WSText.ParagraphSm>

      <WSPanel
        shimmer={
          queryPayeeEngagements.isLoading || outstandingInvoicesQuery.isLoading
        }
      >
        <ContractorInformation payeeId={payeeId} />
        {visibleEngagements?.length ? (
          <WSElement mt="2XL">
            <WSText.ParagraphSm color="gray500">
              {`${visibleEngagements.length} engagement${
                visibleEngagements.length === 1 ? "" : "s"
              } will be deactivated:`}
            </WSText.ParagraphSm>
            {visibleEngagements.slice(0, 5).map((e, index) => (
              <WSText.ParagraphSm mt="S" key={e.payerPayeeEngagementId}>
                {selectorPayerPayeeEngagementName(e, true)}
              </WSText.ParagraphSm>
            ))}
            {visibleEngagements.length > 5 ? (
              <WSText.ParagraphSm mt="S" color="gray500">
                + {visibleEngagements.length - 5} more
              </WSText.ParagraphSm>
            ) : null}
          </WSElement>
        ) : null}
      </WSPanel>

      <WSAlert
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving contractor"
      >
        After a contractor is archived, no new payments can be created or sent
        to the contractor. The contractor will be made archived and hidden from
        engagements they were on.
        <WSElement mt="M" />
        Existing invoices and the contractor profile will remain visible until
        you delete them. You can always restore the contractor when needed. The
        contractor’s eligibility status will be recalculated upon restore.
      </WSAlert>

      {hasOutstandingInvoices && (
        <WSAlert
          theme="warning"
          icon="alert"
          size="M"
          title="Outstanding invoices exist"
        >
          The contractor cannot be archived at this time. All outstanding
          invoices for this contractor must be resolved before archiving the
          contractor.{" "}
          <WSText.ParagraphSm
            inline
            color="blue400"
            onClick={() => {
              const query = queryString.stringify({
                payeeId
              });
              history.push(`/member/invoices/payables/all?${query}`);
            }}
          >
            See all
          </WSText.ParagraphSm>
        </WSAlert>
      )}

      <WSErrorMessage
        error={archivePayeeMeta.error}
        contextKey="ArchiveClient"
      />

      <WSActions
        alignment="fill"
        disabled={
          !outstandingInvoicesQuery.isSuccess ||
          !queryPayeeEngagements.isSuccess
        }
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive contractor",
            loading: archivePayeeMeta.isLoading,
            disabled: hasOutstandingInvoices,
            onAsyncClick: async () => {
              if (visibleEngagements?.length) {
                await archiveEngagements({
                  payeeId,
                  engagementIds: visibleEngagements.map(
                    e => e.payerPayeeEngagementId
                  )
                });
              }

              await archivePayee(
                {
                  payeeId,
                  xWingspanAccount
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      duration: 5000,
                      type: "success",
                      message: "Contractor archived successfully"
                    });
                    onClose();
                  },
                  onError: () => {
                    openSnackbar({
                      duration: 5000,
                      type: "warning",
                      message: `Failed to archive contractor`
                    });
                  }
                }
              );
            }
          }
        ]}
      />
    </WSList>
  );
};

export const useArchiveContractorModal = () =>
  useWSModal(ArchiveContractorModal, {
    title: "Archive contractor",
    size: "M"
  });
